/**
 * The dashboard's default layout, and where a widget the reader's saved
 * layout has never heard of goes — the pure half of WidgetManagerContext.
 *
 * ORDER, NOT A GRID. The dashboard sorts enabled widgets by (row, col) and
 * round-robins them into masonry columns (lib/widgetOrder), so a position is
 * only ever a sort key. The default layout is WIDGET_ORDER (shared/widgets.ts)
 * written out as sort keys: the picker's category order, summary to detail.
 */
import { WIDGET_DEFINITIONS, WIDGET_ORDER } from "@shared/widgets";

export interface WidgetPosition {
  row: number;
  col: number;
  width: number;
  height: number;
}

export interface WidgetPreference {
  id: string;
  enabled: boolean;
  position?: WidgetPosition;
  configuration?: Record<string, any>;
}

/**
 * A new user's dashboard, and Reset to Defaults. Key Numbers is row 0 (it is
 * pinned full-width above the grid anyway); everything else follows in
 * WIDGET_ORDER, three to a row, so the sort reproduces the order exactly.
 */
export function defaultWidgetLayout(): Record<string, WidgetPreference> {
  const layout: Record<string, WidgetPreference> = {};
  let k = 0;
  for (const id of WIDGET_ORDER) {
    const def = WIDGET_DEFINITIONS[id];
    const position: WidgetPosition =
      id === "project-numbers"
        ? { row: 0, col: 1, width: 12, height: 2 }
        : { row: 1 + Math.floor(k / 3), col: (k % 3) * 4 + 1, width: 4, height: 3 };
    if (id !== "project-numbers") k += 1;
    layout[id] = { id, enabled: def.defaultEnabled, position };
  }
  return layout;
}

/**
 * Widgets a SAVED layout does not mention yet — ones added since it was saved.
 *
 * They go straight after Needs Attention, in WIDGET_ORDER, on or off as the
 * definition says — the precedent Key Numbers and Needs Attention set on
 * 2026-09-28, when they reached every existing dashboard at the top without a
 * version bump. Nothing the reader placed moves: a new widget takes a sort key
 * BETWEEN Needs Attention's and the next one's (a fractional column), so the
 * relative order of everything already saved is exactly what it was.
 *
 * Bumping WIDGET_LAYOUT_VERSION instead would have reset people's layouts to
 * do the same thing. A saved layout cannot mention an id that did not exist
 * when it was saved, so "missing" is exactly "new since then".
 */
export function mergeNewWidgets(saved: Readonly<Record<string, WidgetPreference>>): Record<string, WidgetPreference> {
  const merged: Record<string, WidgetPreference> = { ...saved };
  const anchor = saved["needs-attention"]?.position ?? { row: 0, col: 5, width: 4, height: 3 };
  WIDGET_ORDER.forEach((id, rank) => {
    if (merged[id]) return;
    merged[id] = {
      id,
      enabled: WIDGET_DEFINITIONS[id].defaultEnabled,
      position: { row: anchor.row, col: anchor.col + (rank + 1) / 1000, width: 4, height: 3 },
      configuration: {},
    };
  });
  return merged;
}
